import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

import {
    DAY_KEYS,
    DAY_NAMES,
    MINUTES_PER_DAY,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_constants';
import { type AvailabilityStatus } from '@apps/web/components/caregivers/caregiver_availability_schedule_types';

export function getAvailabilityType(
    status: AvailabilityStatus,
): 'availability' | 'unavailability' {
    return status === 'unavailable' ? 'unavailability' : 'availability';
}

export function formatScheduleTimeValue(
    value: string,
    timezone: string,
    { isEndTime = false }: { readonly isEndTime?: boolean } = {},
): string {
    const displayDate =
        isEndTime && formatInTimeZone(value, timezone, 'HH:mm') === '00:00'
            ? new Date(new Date(value).getTime() - 60_000)
            : value;

    return formatInTimeZone(displayDate, timezone, 'h:mm a');
}

function getWeeklyDisplayMinutes(
    minutes: number,
    { isEndTime = false }: { readonly isEndTime?: boolean } = {},
): number {
    if (isEndTime && minutes > 0 && minutes % MINUTES_PER_DAY === 0) {
        return minutes - 1;
    }
    return minutes;
}

export function formatTimeRange(
    startTime: string,
    endTime: string,
    timezone: string,
): string {
    return `${formatScheduleTimeValue(
        startTime,
        timezone,
    )} - ${formatScheduleTimeValue(endTime, timezone, { isEndTime: true })}`;
}

export function getWeeklyLabel(
    minutes: number,
    referenceWeekStartKey: string,
    timezone: string,
    { isEndTime = false }: { readonly isEndTime?: boolean } = {},
) {
    const totalMinutes = Math.max(
        0,
        getWeeklyDisplayMinutes(minutes, { isEndTime }),
    );
    const dayIndex = Math.floor(totalMinutes / MINUTES_PER_DAY);
    const normalizedDayIndex = ((dayIndex % 7) + 7) % 7;
    const dayLabel = DAY_NAMES[normalizedDayIndex] || 'Monday';

    const baseDate = fromZonedTime(
        `${referenceWeekStartKey}T00:00:00`,
        timezone,
    );
    const dateTime = new Date(baseDate.getTime() + totalMinutes * 60_000);
    const timeLabel = formatScheduleTimeValue(dateTime.toISOString(), timezone);

    return { dayLabel, timeLabel };
}

export function formatWeeklyTimeRange(
    startMinuteOfWeek: number,
    endMinuteOfWeek: number,
    referenceWeekStartKey: string,
    timezone: string,
): string {
    const start = getWeeklyLabel(
        startMinuteOfWeek,
        referenceWeekStartKey,
        timezone,
    );
    const end = getWeeklyLabel(
        endMinuteOfWeek,
        referenceWeekStartKey,
        timezone,
        { isEndTime: true },
    );
    return `${start.timeLabel} - ${end.timeLabel}`;
}

export function getWeeklyRangeLabel(
    startMinuteOfWeek: number,
    endMinuteOfWeek: number,
    referenceWeekStartKey: string,
    timezone: string,
) {
    const startLabel = getWeeklyLabel(
        startMinuteOfWeek,
        referenceWeekStartKey,
        timezone,
    );
    const endLabel = getWeeklyLabel(
        endMinuteOfWeek,
        referenceWeekStartKey,
        timezone,
        { isEndTime: true },
    );

    return {
        dayLabel:
            startLabel.dayLabel === endLabel.dayLabel
                ? startLabel.dayLabel
                : `${startLabel.dayLabel} - ${endLabel.dayLabel}`,
        timeLabel: `${startLabel.timeLabel} - ${endLabel.timeLabel}`,
    };
}

export function minuteOfWeekToTime(
    minutes: number,
    { isEndTime = false }: { readonly isEndTime?: boolean } = {},
): string {
    const minuteOfDay = minutes % MINUTES_PER_DAY;
    if (minuteOfDay === 0 && minutes > 0 && isEndTime) {
        return '24:00';
    }
    const hours = Math.floor(minuteOfDay / 60);
    const mins = minuteOfDay % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

export function minuteOfWeekToDayKey(minutes: number): string {
    const dayIndex = Math.floor(minutes / MINUTES_PER_DAY) % 7;
    return DAY_KEYS[dayIndex] || 'monday';
}
